// v1.28.82 — Party MERGE workflow. Admin picks a Source party (deleted
// after merge) and a Target party (kept). We preview per-collection row
// counts, outstanding balances, and require a type-to-confirm before
// firing the merge. Backend endpoint reassigns party_id on every tenant
// collection, disables the source's portal login, deletes the source,
// and writes a permanent audit-log entry.

import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
    ArrowsMerge, ArrowLeft, Warning, MagnifyingGlass, User, CaretRight,
} from "@phosphor-icons/react";

const INR = (n) => "₹" + (Number(n || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function PartyPicker({ label, testId, value, onChange, parties, excludeId, tone }) {
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const filtered = useMemo(() => {
        const query = q.trim().toLowerCase();
        return parties
            .filter((p) => p.id !== excludeId)
            .filter((p) => !query || (p.name || "").toLowerCase().includes(query) || (p.phone || "").includes(query))
            .slice(0, 30);
    }, [parties, q, excludeId]);
    const selected = parties.find((p) => p.id === value);
    return (
        <div className="relative">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</Label>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid={`${testId}-btn`}
                className={`mt-1 flex w-full items-center gap-2 rounded-md border bg-white px-3 py-2 text-left text-sm shadow-sm ${tone === "danger" ? "border-rose-300" : "border-slate-300"} hover:bg-slate-50`}
            >
                <User size={14} className={tone === "danger" ? "text-rose-500" : "text-emerald-600"} weight="bold" />
                <span className={`flex-1 truncate ${selected ? "text-slate-900" : "text-slate-400"}`}>
                    {selected ? selected.name : "Choose party…"}
                </span>
                <CaretRight size={12} className={`text-slate-400 transition ${open ? "rotate-90" : ""}`} />
            </button>
            {open && (
                <div className="absolute z-40 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-xl" data-testid={`${testId}-panel`}>
                    <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
                        <MagnifyingGlass size={14} className="text-slate-400" />
                        <input
                            autoFocus
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search by name or phone…"
                            className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
                            data-testid={`${testId}-search`}
                        />
                    </div>
                    <div className="max-h-64 overflow-y-auto py-1">
                        {filtered.length === 0 ? (
                            <div className="px-3 py-3 text-xs text-slate-400">No matches</div>
                        ) : filtered.map((p) => (
                            <button
                                type="button"
                                key={p.id}
                                onClick={() => { onChange(p.id); setOpen(false); setQ(""); }}
                                data-testid={`${testId}-opt-${p.id}`}
                                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50"
                            >
                                <span className="flex-1 truncate">{p.name}</span>
                                {p.phone && <span className="text-[11px] text-slate-400 font-mono">{p.phone}</span>}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function StatCard({ label, value, tone }) {
    return (
        <div className={`rounded-md border px-3 py-2 ${tone === "danger" ? "border-rose-200 bg-rose-50/50" : tone === "good" ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 bg-slate-50"}`}>
            <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="mt-0.5 text-sm font-bold text-slate-800 tabular-nums">{value}</div>
        </div>
    );
}

export default function PartyMerge() {
    const [parties, setParties] = useState([]);
    const [sourceId, setSourceId] = useState("");
    const [targetId, setTargetId] = useState("");
    const [preview, setPreview] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/parties");
                // Endpoint returns {parties:[...] } OR a plain array — normalise.
                const rows = Array.isArray(data) ? data : (data?.parties || []);
                setParties(rows);
            } catch (e) { toast.error(formatApiError(e)); }
        })();
    }, []);

    useEffect(() => {
        if (!sourceId || !targetId) { setPreview(null); setConfirmText(""); return; }
        if (sourceId === targetId) { setPreview(null); return; }
        setLoadingPreview(true);
        (async () => {
            try {
                const { data } = await api.get(`/parties/${sourceId}/merge-preview/${targetId}`);
                setPreview(data);
                setConfirmText("");
            } catch (e) {
                toast.error(formatApiError(e));
                setPreview(null);
            } finally { setLoadingPreview(false); }
        })();
    }, [sourceId, targetId]);

    const confirmOk = preview && confirmText.trim().toLowerCase() === (preview.source.name || "").trim().toLowerCase();

    const doMerge = async () => {
        if (!preview || !confirmOk || busy) return;
        setBusy(true);
        try {
            const { data } = await api.post("/parties/merge", {
                source_id: sourceId,
                target_id: targetId,
                confirm_name: confirmText,
            });
            const total = data?.total_moved || 0;
            toast.success(`Merged "${data.source_name}" → "${data.target_name}" · ${total} record${total === 1 ? "" : "s"} moved`);
            // Navigate to target party's ledger
            navigate(`/admin/transactions?party_id=${targetId}`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="mx-auto max-w-4xl space-y-5" data-testid="party-merge-page">
            <div>
                <Link to="/admin/parties" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-800" data-testid="party-merge-back">
                    <ArrowLeft size={12} /> Back to Parties
                </Link>
                <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
                    <ArrowsMerge size={26} weight="fill" className="text-violet-700" />
                    Merge Parties
                </h1>
                <p className="mt-1 max-w-2xl text-sm text-slate-600">
                    Move all transactions, uploads, ledger entries, pricing rules and notifications from a <b>Source</b> party onto a <b>Target</b> party. The source is deleted afterwards and any portal login it had is disabled. The audit trail keeps a permanent record even after the source row is gone.
                </p>
            </div>

            <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-2">
                <PartyPicker
                    label="Source Party (will be deleted)"
                    testId="party-merge-source"
                    value={sourceId}
                    onChange={setSourceId}
                    parties={parties}
                    excludeId={targetId}
                    tone="danger"
                />
                <PartyPicker
                    label="Target Party (kept)"
                    testId="party-merge-target"
                    value={targetId}
                    onChange={setTargetId}
                    parties={parties}
                    excludeId={sourceId}
                    tone="good"
                />
            </div>

            {loadingPreview && (
                <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500 shadow-sm">Loading preview…</div>
            )}

            {preview && (
                <div className="space-y-4" data-testid="party-merge-preview">
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="rounded-xl border border-rose-200 bg-rose-50/30 p-4 shadow-sm">
                            <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-rose-700">Source · will be deleted</div>
                            <div className="text-lg font-bold text-slate-900">{preview.source.name}</div>
                            <div className="mt-0.5 text-xs text-slate-500 font-mono">{preview.source.phone || "no phone"}</div>
                            <div className="mt-3 grid gap-2 sm:grid-cols-2">
                                <StatCard label="Transactions" value={preview.source.txns.toLocaleString("en-IN")} tone="danger" />
                                <StatCard label="Outstanding" value={INR(preview.source.outstanding)} tone="danger" />
                                {preview.source.has_portal_login && (
                                    <div className="col-span-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                                        <b>Portal login</b> attached — it will be disabled and relinked to <i>{preview.target.name}</i>.
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50/30 p-4 shadow-sm">
                            <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700">Target · kept</div>
                            <div className="text-lg font-bold text-slate-900">{preview.target.name}</div>
                            <div className="mt-0.5 text-xs text-slate-500 font-mono">{preview.target.phone || "no phone"}</div>
                            <div className="mt-3 grid gap-2 sm:grid-cols-2">
                                <StatCard label="Transactions" value={preview.target.txns.toLocaleString("en-IN")} tone="good" />
                                <StatCard label="Outstanding" value={INR(preview.target.outstanding)} tone="good" />
                            </div>
                        </div>
                    </div>

                    <div className="rounded-xl border-2 border-violet-300 bg-violet-50 p-4 shadow-sm" data-testid="party-merge-combined">
                        <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-violet-800">Combined totals after merge</div>
                        <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                                <div className="text-[9px] uppercase text-violet-700">Transactions</div>
                                <div className="text-2xl font-bold text-violet-900 tabular-nums" data-testid="party-merge-combined-txns">{preview.combined.txns.toLocaleString("en-IN")}</div>
                            </div>
                            <div>
                                <div className="text-[9px] uppercase text-violet-700">Outstanding</div>
                                <div className="text-2xl font-bold text-violet-900 tabular-nums" data-testid="party-merge-combined-outstanding">{INR(preview.combined.outstanding)}</div>
                            </div>
                        </div>
                        <div className="mt-3 grid gap-1 text-[11px] text-slate-600 sm:grid-cols-3">
                            <div><b>Uploads moved:</b> {preview.counts.uploads}</div>
                            <div><b>Pricing rules:</b> {preview.counts.pricing_rules}</div>
                            <div><b>Verification queue:</b> {preview.counts.verification_queue}</div>
                            <div><b>Notifications:</b> {preview.counts.notifications}</div>
                            <div><b>Portal logins:</b> {preview.counts.portal_logins}</div>
                        </div>
                    </div>

                    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 shadow-sm" data-testid="party-merge-warning">
                        <div className="flex items-start gap-2">
                            <Warning size={22} weight="fill" className="mt-0.5 shrink-0 text-amber-600" />
                            <div className="text-sm text-amber-900">
                                <div className="font-bold uppercase tracking-wide">This cannot be undone</div>
                                <div className="mt-1">
                                    All transactions from <b>{preview.source.name}</b> will be moved to <b>{preview.target.name}</b>. <b>{preview.source.name}</b> will be permanently deleted.
                                </div>
                                <div className="mt-3">
                                    <Label className="text-[10px] uppercase tracking-wider text-amber-800">Type the source name to confirm</Label>
                                    <Input
                                        value={confirmText}
                                        onChange={(e) => setConfirmText(e.target.value)}
                                        placeholder={preview.source.name}
                                        data-testid="party-merge-confirm-input"
                                        className={`mt-1 bg-white ${confirmText && !confirmOk ? "border-rose-400" : ""}`}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline"
                                onClick={() => { setSourceId(""); setTargetId(""); }}
                                disabled={busy}
                                data-testid="party-merge-cancel">
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={doMerge}
                            disabled={!confirmOk || busy}
                            data-testid="party-merge-execute"
                            className="bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                        >
                            <ArrowsMerge size={14} weight="bold" className="mr-1" />
                            {busy ? "Merging…" : "Merge & delete source"}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
